import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { widgetDefault } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/TopBannerWidgetConfigurationPage/constants'
import configurationServices from '@/configuration/services/services'

import { getSavePreferencesPayload } from './helpers'

const useTopBannerWidgetConfiguration = ({ storeId, setToastInfo, supportedLanguages }) => {
  const { t } = useTranslation()
  const { data: topBannerWidgetPreferences, loading: gettingTopBannerWidgetPreferences } = useFetch(
    () => configurationServices.getTopBannerWidgetPreferences({ storeId }),
    {
      doInitialRequest: true,
    }
  )
  const { fetchData: editTopBannerWidgetPreferences, loading: applyingTopBannerWidgetPreferences } = useFetch(
    configurationServices.editTopBannerWidgetPreferences,
    {
      doInitialRequest: false,
    }
  )
  const [backgroundColor, setBackgroundColor] = useState(widgetDefault.background)
  const [textColor, setTextColor] = useState(widgetDefault.text)
  const [linkColor, setLinkColor] = useState(widgetDefault.link)
  const [customTexts, setCustomTexts] = useState({ text: {} })
  const [useCustomText, setUseCustomText] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const setInitValues = useCallback(() => {
    setBackgroundColor(topBannerWidgetPreferences?.background_color || widgetDefault.background)
    setTextColor(topBannerWidgetPreferences?.text_color || widgetDefault.text)
    setLinkColor(topBannerWidgetPreferences?.link_color || widgetDefault.link)

    setCustomTexts({
      text: topBannerWidgetPreferences?.text || {},
    })
    setUseCustomText(Object.values(topBannerWidgetPreferences?.text || {}).some(value => value !== null))

    setHasChanges(false)
  }, [topBannerWidgetPreferences])

  useEffect(() => {
    setInitValues()
  }, [setInitValues, topBannerWidgetPreferences])

  const revertChanges = () => {
    setInitValues()
  }

  const savePreferences = async () => {
    const payload = getSavePreferencesPayload({
      backgroundColor,
      textColor,
      linkColor,
      useCustomText,
      customTexts,
      supportedLanguages,
    })
    await editTopBannerWidgetPreferences({ storeId, preferences: payload })
    setHasChanges(false)
  }

  const scriptInfo = `<div id="reveni-top-banner-widget"></div>
  <script defer="defer" src='${import.meta.env.VITE_REVENI_TOP_BANNER_WIDGET}?language={{ request.locale.iso_code }}&configuration={{ shop.metafields.reveni.top_banner_widget.value | json }}'></script>`

  const onSave = async () => {
    try {
      await savePreferences()
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, type: 'error', message: error.errorMessage })
    }
  }

  return {
    gettingTopBannerWidgetPreferences,
    applyingTopBannerWidgetPreferences,
    backgroundColor,
    setBackgroundColor: value => {
      setHasChanges(true)
      setBackgroundColor(value)
    },
    textColor,
    setTextColor: value => {
      setHasChanges(true)
      setTextColor(value)
    },
    linkColor,
    setLinkColor: value => {
      setHasChanges(true)
      setLinkColor(value)
    },
    customTexts,
    setCustomTexts: value => {
      setHasChanges(true)
      setCustomTexts(value)
    },
    useCustomText,
    setUseCustomText: value => {
      setHasChanges(true)
      setUseCustomText(value)
    },
    hasChanges,
    revertChanges,
    scriptInfo,
    onSave,
  }
}

export default useTopBannerWidgetConfiguration
